import { ref } from 'vue'
import type { SupabaseClient, RealtimeChannel } from '@supabase/supabase-js'
import { useBalanceStore } from '~/stores/balance'
import { useBonusStore } from '~/stores/bonus'
import { reportFallback, trackSupabaseChannelStatus } from '~/utils/reconnectTelemetry'

/**
 * CLIENT-ONLY COMPOSABLE
 *
 * Module-level state is intentional for singleton pattern - ensures a single
 * shared state across all component instances using this composable.
 *
 * Uses the broadcast pattern (not postgres_changes) because RLS policies on
 * the balances table are service_role only. The backend sends balance:updated
 * events via httpBroadcast after any balance change.
 */
let channel: RealtimeChannel | null = null
let currentUserId: string | null = null
const isSubscribed = ref(false)

// Tab visibility: re-fetch balance after returning from background
let balanceTabHiddenAt = 0
let balanceVisibilityHandler: (() => void) | null = null

export function useBalanceRealtime() {
  const { $supabase } = useNuxtApp()
  const supabase = $supabase as SupabaseClient
  const balanceStore = useBalanceStore()
  const bonusStore = useBonusStore()

  function subscribe(userId: string): void {
    if (channel && currentUserId === userId) return
    if (channel && currentUserId !== userId) unsubscribe()

    currentUserId = userId
    const channelName = `balance-updates-${userId}`

    // private:true gates this user-scoped topic via messages_owner_topics_select
    // RLS so only the owner can subscribe (closes M-3 impersonation, §5.12).
    channel = supabase
      .channel(channelName, { config: { private: true } })
      .on('broadcast', { event: 'balance:updated' }, async () => {
        // Balance changed — re-fetch from API to get accurate numbers
        try {
          if ($supabase) {
            const { data: { session } } = await $supabase.auth.getSession()
            const token = session?.access_token ?? null
            if (token) {
              balanceStore.fetchBalance(token)
              // While a bonus is active, the same balance change means a bet
              // settled — refresh wagering status so the in-game bonus strip
              // and wallet progress bar advance. Gated on hasActiveBonus so
              // non-bonus players never take the extra request; the fetch that
              // finally returns 0 grants (conversion/forfeit) still runs because
              // the flag is still true at that event, then flips off after.
              if (bonusStore.hasActiveBonus) bonusStore.fetchStatus(token)
            }
          }
        } catch { /* silent — will sync on next event or page load */ }

        // Same backend event also signals that the user's transaction
        // history may be stale (withdrawal status flips, refund inserts).
        // Components that don't manage the balance store (e.g. the
        // TransactionHistory list) listen for this window event and refetch
        // their own data without taking another realtime subscription.
        // Window events are cheap and decouple subscribers from the channel.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('wallet:tx-list-stale'))
        }
      })
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          isSubscribed.value = true
          setupBalanceVisibilityHandler()
        } else if (status === 'CHANNEL_ERROR') {
          isSubscribed.value = false
          console.error('[BalanceRealtime] Channel error:', err?.message || 'Unknown error')
        } else if (status === 'CLOSED') {
          isSubscribed.value = false
        }
        trackSupabaseChannelStatus('balance', status, err?.message)
      })
  }

  /**
   * Set up visibility handler to re-fetch balance on tab return (>5s hidden).
   * Compensates for any events missed while tab was in background.
   */
  function setupBalanceVisibilityHandler(): void {
    if (balanceVisibilityHandler || typeof document === 'undefined') return

    balanceVisibilityHandler = async () => {
      if (document.hidden) {
        balanceTabHiddenAt = Date.now()
      } else {
        const hidden = balanceTabHiddenAt ? Date.now() - balanceTabHiddenAt : 0
        balanceTabHiddenAt = 0
        if (hidden > 5000 && currentUserId) {
          // Tab-visibility REST refetch compensating for events missed while hidden.
          reportFallback('balance', 'tab_refetch', 'tab_return', { hiddenMs: hidden })
          try {
            if ($supabase) {
              const { data: { session } } = await $supabase.auth.getSession()
              const token = session?.access_token ?? null
              if (token) balanceStore.fetchBalance(token)
            }
          } catch { /* silent — balance will sync on next event */ }
        }
      }
    }
    document.addEventListener('visibilitychange', balanceVisibilityHandler)
  }

  function cleanupBalanceVisibilityHandler(): void {
    if (!balanceVisibilityHandler || typeof document === 'undefined') return
    document.removeEventListener('visibilitychange', balanceVisibilityHandler)
    balanceVisibilityHandler = null
    balanceTabHiddenAt = 0
  }

  function unsubscribe(): void {
    cleanupBalanceVisibilityHandler()
    if (channel) {
      supabase.removeChannel(channel)
      channel = null
      currentUserId = null
      isSubscribed.value = false
    }
  }

  return {
    isSubscribed,
    subscribe,
    unsubscribe,
  }
}
